import { z } from 'zod';

import { zodAddressSchema } from '@vestigas/data';

import { EmailSchema, toSnakeCase } from '@vestigas/utils';
import { i18n } from '~/utils';

import { HISTORY_ACTORS } from '../contract/constants';
import { zodDeliveryAddressSchema } from '../contract/validations';

import {
  BUYER_ACTION,
  BUYER_RESPOND_ACTION,
  CALL_OFF_STATUS,
  DELIVERY_SITE_SOURCE,
  SUPPLIER_ACTION,
  SUPPLIER_RESPOND_ACTION,
} from './constants';

// ============================================================================
// Delivery Date Validation
// ============================================================================

/**
 * Checks if a value is a non-empty ISO date string.
 */
const isValidDateString = (
  value: string | null | undefined,
): value is string => {
  return typeof value === 'string' && value.length > 0;
};

/** Type for schemas that include delivery date fields */
type WithDeliveryDate = {
  deliveryDateFrom?: string | null;
  deliveryDateUntil?: string | null;
};

/**
 * Refinement function that validates at least one of deliveryDateFrom or deliveryDateUntil is provided.
 */
export function refineAtLeastOneDeliveryDate<T extends WithDeliveryDate>(
  schema: z.ZodType<T>,
) {
  return schema.refine(
    (data) =>
      isValidDateString(data.deliveryDateFrom) ||
      isValidDateString(data.deliveryDateUntil),
    {
      message: i18n.t(($) => $.form.error.atLeastOneDeliveryDateRequired, {
        ns: 'callOff',
      }),
      path: ['deliveryDateFrom'],
    },
  );
}

/**
 * Refinement function that validates deliveryDateUntil >= deliveryDateFrom.
 * ISO date strings (YYYY-MM-DD) sort lexicographically in date order.
 */
export function refineDeliveryDateFromUntil<T extends WithDeliveryDate>(
  schema: z.ZodType<T>,
) {
  return schema.refine(
    (data) => {
      if (
        !isValidDateString(data.deliveryDateFrom) ||
        !isValidDateString(data.deliveryDateUntil)
      ) {
        return true;
      }

      return data.deliveryDateUntil >= data.deliveryDateFrom;
    },
    {
      message: i18n.t(($) => $.form.error.dateUntilMustBeAfterDateFrom, {
        ns: 'callOff',
      }),
      path: ['deliveryDateUntil'],
    },
  );
}

export function refineDeliveryLocationExists(schema: z.ZodType) {
  return schema.refine(
    (data) => {
      const hasDeliveryLocation =
        [
          ...Object.values(data.deliveryAddress ?? {}),
          data.deliverySiteId,
          data.deliverySiteText,
        ]
          .map((value) => (typeof value === 'string' ? value.trim() : value))
          .filter(Boolean).length > 0;

      return hasDeliveryLocation;
    },
    {
      message: i18n.t(($) => $.form.error.deliveryAddressOrSiteIdRequired, {
        ns: 'callOff',
      }),
      path: ['deliveryAddress'], // Error display is handled in FormSectionDeliveryLocation; error here is just for validation
    },
  );
}

// ============================================================================
// Delivery Time Validation
// ============================================================================

// Time format regex: HH:MM (24-hour format)
const timeFormatRegex = /^([01]?\d|2[0-3]):[0-5]\d$/;

/**
 * Checks if a value is a valid time string (non-empty and matches format).
 */
const isValidTimeString = (
  value: string | null | undefined,
): value is string => {
  return typeof value === 'string' && timeFormatRegex.test(value);
};

/**
 * Parses a time string (HH:MM) into minutes since midnight for comparison.
 * Assumes the time string has already been validated by the regex.
 */
function parseTimeToMinutes(time: string): number {
  const parts = time.split(':');
  const hours = Number(parts[0]);
  const minutes = Number(parts[1]);

  return hours * 60 + minutes;
}

/**
 * Optional time field schema.
 * The native <input type="time"> already guarantees valid HH:MM format,
 * so the schema only needs to accept string | null | undefined.
 */
export const optionalTimeSchema = z.string().nullish();

/**
 * Optional delivery date field schema.
 * Accepts ISO date strings, empty string (cleared input), null, and undefined.
 * Empty strings are transformed to null so callers always receive a clean value.
 */
export const optionalDeliveryDateSchema = z
  .union([z.iso.date(), z.literal('')])
  .nullish()
  .transform((value) => (value === '' ? null : value));

/**
 * Base schema for delivery time range (from/until) without cross-field validation.
 * Use this when composing with other schemas, then apply `refineDeliveryTimeFromUntil`.
 */
export const DeliveryTimeFromUntilBaseSchema = z.object({
  deliveryTimeFrom: optionalTimeSchema,
  deliveryTimeUntil: optionalTimeSchema,
});

/** Type for schemas that include delivery time fields */
type WithDeliveryTime = {
  deliveryTimeFrom?: string | null;
  deliveryTimeUntil?: string | null;
};

/**
 * Refinement function that validates deliveryTimeUntil >= deliveryTimeFrom.
 * Use this to add time range validation to any schema that includes the time fields.
 *
 * @example
 * // Compose with another schema
 * const MySchema = refineDeliveryTimeFromUntil(
 *   z.object({
 *     notes: z.string(),
 *   }).merge(DeliveryTimeFromUntilBaseSchema)
 * );
 */
export function refineDeliveryTimeFromUntil<T extends WithDeliveryTime>(
  schema: z.ZodType<T>,
) {
  return schema.refine(
    (data) => {
      // Only validate comparison if both values are valid time strings
      if (
        !isValidTimeString(data.deliveryTimeFrom) ||
        !isValidTimeString(data.deliveryTimeUntil)
      ) {
        return true;
      }

      return (
        parseTimeToMinutes(data.deliveryTimeUntil) >=
        parseTimeToMinutes(data.deliveryTimeFrom)
      );
    },
    {
      message: i18n.t(($) => $.form.error.timeUntilMustBeAfterTimeFrom, {
        ns: 'callOff',
      }),
      path: ['deliveryTimeUntil'],
    },
  );
}

/**
 * Applies both date range and time range cross-field validations.
 * Use this for schemas that contain both delivery date and delivery time fields.
 */
export function refineDeliveryDateTimeRanges<
  T extends WithDeliveryDate & WithDeliveryTime,
>(schema: z.ZodType<T>) {
  return refineDeliveryDateFromUntil(refineDeliveryTimeFromUntil(schema));
}

/**
 * Complete schema for validating delivery time range (from/until).
 * Validates:
 * 1. Both times are optional (nullish)
 * 2. If provided, times must be in HH:MM format (24-hour)
 * 3. If both are provided, deliveryTimeUntil >= deliveryTimeFrom
 */
export const DeliveryTimeFromUntilSchema = refineDeliveryTimeFromUntil(
  DeliveryTimeFromUntilBaseSchema,
);

// ============================================================================
// Shared Schemas
// ============================================================================

/**
 * Shared validation schemas for call-off-related API calls.
 * Only contains schemas used by multiple hooks.
 */

// Re-export delivery address schema from contract for convenience
export { zodDeliveryAddressSchema };

// Call-off status enum
export const zodCallOffStatusSchema = z.enum(CALL_OFF_STATUS);

// Contact info schema (used for calloff_contact, onsite_contact, issuer_contact request payloads,
// where the form requires a name and email to be filled in before submission)
// Note: This is different from contract's zodContractContactSchema which has party/role fields
// Not exported - only used as a base for the payload schemas below within this file.
const zodContactInfoSchema = z.object({
  email: EmailSchema,
  firstName: z.string().trim(),
  lastName: z.string().trim(),
  phone: z.string().trim().nullish(),
});

// Contact info response schema. The API's ContactInfo schema only guarantees the computed
// `name` field - first_name/last_name/email/phone are nullable (e.g. a contact created
// before name capture was added, or an issuer without a phone on file).
export const zodContactInfoResponseSchema = z.object({
  email: EmailSchema.nullish(),
  firstName: z.string().trim().nullish(),
  lastName: z.string().trim().nullish(),
  phone: z.string().trim().nullish(),
});

export const zodContactInfoSchemaWithEmails = zodContactInfoSchema.extend({
  receiveReminderEmails: z.boolean().prefault(false),
  receiveUpdateEmails: z.boolean().prefault(true),
});

// Response variant of zodContactInfoSchemaWithEmails (used for issuerContact responses).
// Not exported - only used within this file.
const zodContactInfoResponseSchemaWithEmails =
  zodContactInfoResponseSchema.extend({
    receiveReminderEmails: z.boolean().prefault(false),
    receiveUpdateEmails: z.boolean().prefault(true),
  });

// Call-off item response schema (from API response) - used by multiple hooks
export const zodCallOffItemResponseSchema = z.object({
  buyerArticleId: z.string().nullish(),
  callOffId: z.uuid(),
  contractItemId: z.uuid(),
  createdOn: z.iso.datetime({ offset: true }),
  currency: z.string().default('EUR'),
  id: z.uuid(),
  modifiedOn: z.iso.datetime({ offset: true }),
  notes: z.string().nullish(),
  position: z.number().int(),
  productDescription: z.string().nullish(),
  productName: z.string(),
  quantity: z.string(),
  sellerArticleId: z.string().nullish(),
  storageLocation: z.string().trim().nullish(),
  unitType: z.string(),
});

export const zodCallOffAttachmentSchema = z.object({
  fileId: z.uuid(),
  fileLink: z.url().optional(), // Only included in the response, not in the request
  fileMime: z.string().trim().min(1),
  fileName: z.string().trim().min(1),
});

// Full call-off response schema (returned from get/create/update operations)
export const zodCallOffResponseSchema = z.object({
  attachments: z.array(zodCallOffAttachmentSchema).nullish(),
  buyerCompanyName: z.string().nullish(),
  calloffContact: zodContactInfoResponseSchema.nullish(),
  calloffExpiresAt: z.iso.datetime({ offset: true }).nullish(),
  calloffNumber: z.string(),
  companyAccountId: z.uuid(),
  contractBuyerAssignedId: z.string().nullish(),
  contractId: z.uuid(),
  contractName: z.string().nullish(),
  contractSellerAssignedId: z.string().nullish(),
  createdBy: z.uuid().nullish(),
  createdOn: z.iso.datetime({ offset: true }),
  declineReason: z.string().nullish(),
  deliveryAddress: zodDeliveryAddressSchema.nullish(),
  deliveryDateFrom: z.iso.date().nullish(),
  deliveryDateUntil: z.iso.date().nullish(),
  deliverySiteAddress: zodAddressSchema.nullish(),
  deliverySiteId: z.uuid().nullish(),
  deliverySiteName: z.string().nullish(),
  deliverySiteSource: z.string().nullish(),
  deliverySiteText: z.string().nullish(),
  deliveryTimeFrom: z.string().nullish(),
  deliveryTimeUntil: z.string().nullish(),
  id: z.uuid(),
  issuerContact: zodContactInfoResponseSchemaWithEmails.nullish(),
  items: z.array(zodCallOffItemResponseSchema).nullish(),
  modifiedBy: z.uuid().nullish(),
  modifiedOn: z.iso.datetime({ offset: true }),
  notes: z.string().nullish(),
  onsiteContact: zodContactInfoResponseSchema.nullish(),
  projectReference: z.string().nullish(),
  respondedAt: z.iso.datetime({ offset: true }).nullish(),
  sellerCompanyName: z.string().nullish(),
  status: zodCallOffStatusSchema,
  storageLocation: z.string().trim().nullish(),
  supplierResponseNotes: z.string().nullish(),
});

// Base payload schema with shared fields for call-off items
export const zodCallOffItemPayloadBaseSchema = z.object({
  buyerArticleId: z.string().trim().max(100).nullish(),
  notes: z.string().trim().nullish(),
  position: z.number().int().min(1).nullish(),
  productDescription: z.string().trim().nullish(),
  productName: z.string().trim().min(1).max(500),
  quantity: z.union([z.number().positive(), z.string()]),
  sellerArticleId: z.string().trim().max(100).nullish(),
  storageLocation: z.string().trim().nullish(),
  unitType: z.string().trim(),
});

// Create call-off item payload schema (extends base with required contractItemId)
// Used by both useMutationCreateCallOffItem and useMutationCreateCallOff
export const zodCreateCallOffItemPayloadSchema =
  zodCallOffItemPayloadBaseSchema.extend({
    contractItemId: z.uuid(),
    position: z.number().int().min(1).nullish(),
  });

// Public call-off item schema (limited fields for magic link / external user view)
export const zodCallOffItemPublicViewSchema = z.object({
  buyerArticleId: z.string().trim().nullish(),
  deliverySiteAddress: zodAddressSchema.nullish(),
  id: z.uuid(),
  position: z.number().int(),
  productDescription: z.string().nullish(),
  productName: z.string().trim(),
  quantity: z.string().trim(),
  sellerArticleId: z.string().nullish(),
  storageLocation: z.string().trim().nullish(),
  unitType: z.string().trim(),
});

// Attachment change schema (shared between history endpoints)
export const zodCallOffAttachmentChangeSchema = z.object({
  action: z.enum(['added', 'deleted']),
  fileId: z.string(),
  name: z.string(),
});

// History entry schema for public view (re-uses structure from useQueryCallOffHistory)
// Note: This is a simplified version for the magic link response
export const zodCallOffHistoryEntryPublicSchema = z.object({
  action: z.string(),
  actor: z.enum(HISTORY_ACTORS),
  actorDisplay: z.string().nullish(),
  attachmentChanges: z.array(zodCallOffAttachmentChangeSchema).nullish(),
  createdAt: z.string(),
  notes: z.string().nullish(),
  proposed: z
    .object({
      deliveryDateFrom: z.string().nullish(),
      deliveryDateUntil: z.string().nullish(),
      deliveryTimeFrom: z.string().nullish(),
      deliveryTimeUntil: z.string().nullish(),
      items: z
        .array(
          z.object({
            calloffItemId: z.string(),
            quantity: z.union([z.number(), z.string()]),
          }),
        )
        .nullish(),
    })
    .nullish(),
  snapshot: z.unknown(),
  status: z.string(),
  versionNumber: z.number(),
});

// Public call-off response schema (limited fields for magic link / external user view)
export const zodCallOffPublicViewSchema = z.object({
  attachments: z.array(zodCallOffAttachmentSchema).nullish(),
  availableActions: z.array(z.enum(SUPPLIER_ACTION)),
  buyerCompanyLogo: z.string().nullable(),
  buyerCompanyName: z.string().nullish(),
  calloffContact: zodContactInfoResponseSchema.nullish(),
  calloffNumber: z.string(),
  contractName: z.string().nullish(),
  deliveryAddress: zodDeliveryAddressSchema.nullish(),
  deliveryDateFrom: z.iso.date().nullish(),
  deliveryDateUntil: z.iso.date().nullish(),
  deliveryTimeFrom: z.string().trim().nullish(),
  deliveryTimeUntil: z.string().trim().nullish(),
  history: z.array(zodCallOffHistoryEntryPublicSchema).nullish(),
  id: z.uuid(),
  issuerContact: zodContactInfoResponseSchema.nullish(),
  items: z.array(zodCallOffItemPublicViewSchema),
  notes: z.string().trim().nullish(),
  onsiteContact: zodContactInfoResponseSchema.nullish(),
  projectReference: z.string().trim().nullish(),
  status: zodCallOffStatusSchema,
  storageLocation: z.string().trim().nullish(),
});

// Base payload schema with shared fields for call-offs (without time range validation)
// Use this for extending, then apply refineDeliveryTimeFromUntil at the final level.
export const zodCallOffPayloadFieldsSchema = z.object({
  calloffContact: zodContactInfoSchema,
  deliveryAddress: zodDeliveryAddressSchema,
  deliveryDateFrom: z.iso.date().nullish(),
  deliveryDateUntil: z.iso.date().nullish(),
  deliverySiteId: z.uuid().nullish(),
  deliverySiteSource: z.preprocess(
    (value) => (typeof value === 'string' ? toSnakeCase(value.trim()) : value),
    z.enum(DELIVERY_SITE_SOURCE).nullish(),
  ),
  deliverySiteText: z.string().nullish(),
  deliveryTimeFrom: optionalTimeSchema,
  deliveryTimeUntil: optionalTimeSchema,
  issuerContact: zodContactInfoSchemaWithEmails,
  notes: z.string().trim().nullish(),
  onsiteContact: zodContactInfoSchema.nullish(),
  projectReference: z.string().trim().max(100).nullish(),
  storageLocation: z.string().trim().nullish(),
});

// Base payload schema with date and time range validations applied
// Note: This schema cannot be extended further due to the refinement.
// If you need to extend, use zodCallOffPayloadFieldsSchema and apply refineDeliveryDateTimeRanges after.
export const zodCallOffPayloadBaseSchema = refineDeliveryDateTimeRanges(
  zodCallOffPayloadFieldsSchema,
);

// ============================================================================
// Buyer Actions (Magic Link State Machine)
// ============================================================================

// All available actions enum (returned by GET /magic_link/actions)
export const zodBuyerActionSchema = z.enum(BUYER_ACTION);

// Actions that trigger state transitions (for POST /magic_link/respond)
export const zodBuyerRespondActionSchema = z.enum(BUYER_RESPOND_ACTION);

// Available buyer actions response schema (from GET /magic_link/actions)
export const zodBuyerActionsResponseSchema = z.object({
  availableActions: z.array(zodBuyerActionSchema),
});

// ============================================================================
// Supplier Actions (Magic Link Public API)
// ============================================================================

// All available supplier actions enum
export const zodSupplierActionSchema = z.enum(SUPPLIER_ACTION);

// Actions that trigger state transitions (for POST /magic_link/:calloff_id/respond)
export const zodSupplierRespondActionSchema = z.enum(SUPPLIER_RESPOND_ACTION);

// Available supplier actions response schema
export const zodSupplierActionsResponseSchema = z.array(
  zodSupplierActionSchema,
);

// Proposed item change schema (for confirm_with_changes action)
export const zodProposedItemChangeSchema = z.object({
  calloffItemId: z.uuid(),
  quantity: z.union([z.number().min(0), z.string()]),
});

// Proposed changes fields schema (without refinement, for extending/omitting)
export const zodProposedChangesFieldsSchema = z
  .object({
    deliveryDateFrom: optionalDeliveryDateSchema,
    deliveryDateUntil: optionalDeliveryDateSchema,
    deliveryTimeFrom: optionalTimeSchema,
    deliveryTimeUntil: optionalTimeSchema,
    items: z.array(zodProposedItemChangeSchema).nullish(),
  })
  .strict();

// Proposed changes schema with date and time range validations (for confirm_with_changes action)
// Note: This schema cannot be extended/omitted due to the refinement.
// If you need to extend/omit, use zodProposedChangesFieldsSchema and apply refineDeliveryDateTimeRanges after.
export const zodProposedChangesSchema = refineDeliveryDateTimeRanges(
  zodProposedChangesFieldsSchema,
);

// Buyer action request schema (for POST /magic_link/respond)
export const zodBuyerActionRequestSchema = z.object({
  action: zodBuyerRespondActionSchema,
  disableCc: z.boolean().nullish().prefault(false),
  notes: z.string().trim().nullish(),
  proposedChanges: zodProposedChangesSchema.nullish(),
  supplierEmail: EmailSchema.nullish(), // Required for send_request action
});

// Buyer action response schema (from POST /magic_link/respond)
export const zodBuyerActionResponseSchema = z.object({
  availableActions: z.array(zodBuyerActionSchema),
  calloff: zodCallOffResponseSchema,
  emailSent: z.boolean(),
  message: z.string().trim().nullish(),
});

// Supplier action request schema (for POST /magic_link/:calloff_id/respond)
export const zodSupplierActionRequestSchema = z.object({
  action: zodSupplierRespondActionSchema,
  notes: z.string().trim().nullish(),
  proposedChanges: zodProposedChangesSchema.nullish(),
});

// Supplier action response schema (from POST /magic_link/:calloff_id/respond)
// The API returns the call-off directly with available_actions embedded (flat structure)
export const zodSupplierActionResponseSchema = z.object({
  calloff: zodCallOffPublicViewSchema,
  emailSent: z.boolean(),
  message: z.string().trim().nullish(),
});
